/**
 * Subscription API client.
 * Endpoints:
 *   GET  /bot/api/subscription/              → full page data
 *   POST /bot/api/subscription/checkout      → process changes
 *   PUT  /bot/api/subscription/billing-info  → update billing info
 *   POST /bot/api/subscription/cancel        → cancel subscription
 *   GET  /bot/api/subscription/proration-preview?plan_id=&billing_cycle=
 *   GET  /bot/api/subscription/invoices          → live invoice history
 */

import { getAuthHeaders } from './auth';
import { getBaseUrl } from './utils';

async function parseJson(res) {
  const data = await res.json();
  if (!res.ok) {
    const message = data?.message || data?.error || res.statusText || 'Request failed';
    throw new Error(message);
  }
  if (data.status === 'success' && data.data) {
    return data.data;
  }
  return data.data != null ? data.data : data;
}

/**
 * Fetch public plan listing (no auth required).
 * GET /bot/api/subscription/plans
 * @returns {Promise<{ plans: Array, trial_duration_days: number }>}
 */
export async function getPublicPlans() {
  const base = getBaseUrl();
  const url = `${base}/bot/api/subscription/plans`.replace(/([^:]\/)\/+/g, '$1');
  const res = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  return parseJson(res);
}

/**
 * Fetch full subscription page data.
 */
export async function getSubscriptionData() {
  const base = getBaseUrl();
  const url = `${base}/bot/api/subscription/`.replace(/([^:]\/)\/+/g, '$1');
  const res = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return parseJson(res);
}

/**
 * Process checkout — plan change, credit purchase, addon changes.
 * @param {object} changes - { plan, addons, creditPack, creditMode, billingCycle }
 * @returns {Promise<{ success: boolean, redirectUrl?: string, message?: string }>}
 */
export async function checkout(changes) {
  const base = getBaseUrl();
  const url = `${base}/bot/api/subscription/checkout`.replace(/([^:]\/)\/+/g, '$1');
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(changes),
  });
  return parseJson(res);
}

/**
 * Update billing information.
 * @param {object} data - { company, email, address, taxId }
 */
export async function updateBillingInfo(data) {
  const base = getBaseUrl();
  const url = `${base}/bot/api/subscription/billing-info`.replace(/([^:]\/)\/+/g, '$1');
  const res = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(data),
  });
  return parseJson(res);
}

/**
 * Cancel subscription at period end.
 * @param {object} [survey] Optional cancellation survey
 *   ({ reason, switching_to?, missing_feature?, feedback? }). When omitted the
 *   request is identical to before (empty body) — back-compatible.
 */
export async function cancelSubscription(survey) {
  const base = getBaseUrl();
  const url = `${base}/bot/api/subscription/cancel`.replace(/([^:]\/)\/+/g, '$1');
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...getAuthHeaders(),
    },
    ...(survey ? { body: JSON.stringify(survey) } : {}),
  });
  return parseJson(res);
}

/** Undo a scheduled (period-end) cancellation. Stripe only. */
export async function resumeSubscription() {
  const base = getBaseUrl();
  const url = `${base}/bot/api/subscription/resume`.replace(/([^:]\/)\/+/g, '$1');
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...getAuthHeaders(),
    },
  });
  return parseJson(res);
}

/**
 * Preview proration for a plan change.
 * @param {string} planId
 * @param {string} billingCycle - 'monthly' | 'annual'
 */
/**
 * Fetch live invoice history (Stripe fetches directly from API with pdf_url).
 * @returns {Promise<Array<{ id, date, plan, amount, status, pdfUrl }>>}
 */
export async function getInvoices() {
  const base = getBaseUrl();
  const url = `${base}/bot/api/subscription/invoices`.replace(/([^:]\/)\/+/g, '$1');
  const res = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return parseJson(res);
}

/**
 * Download an invoice PDF. Fetches a presigned URL with auth, then opens it.
 * @param {number} invoiceDbId - The invoice DB ID
 * @param {string} invoiceId - The invoice display ID (e.g. INV-2026-001) for filename
 */
export async function downloadInvoicePdf(invoiceDbId, invoiceId) {
  const base = getBaseUrl();
  const url = `${base}/bot/api/subscription/invoices/${invoiceDbId}/pdf`.replace(/([^:]\/)\/+/g, '$1');
  const res = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  if (!res.ok) throw new Error('Failed to generate invoice PDF');
  const data = await res.json();
  const pdfUrl = data?.data?.url;
  if (!pdfUrl) throw new Error('No PDF URL returned');
  window.open(pdfUrl, '_blank');
}

/**
 * Validate a promo code.
 * @param {string} code
 * @param {string} planId
 * @param {string} billingCycle
 * @returns {Promise<{ code, discountType, discountValue, duration, durationMonths, description }>}
 */
export async function validatePromoCode(code, planId, billingCycle) {
  const base = getBaseUrl();
  const url = `${base}/bot/api/subscription/validate-promo`.replace(/([^:]\/)\/+/g, '$1');
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ code, plan: planId, billingCycle }),
  });
  return parseJson(res);
}

/**
 * Confirm a Stripe Checkout session after redirect.
 * Activates the pending subscription immediately without waiting for the webhook.
 * @param {string} sessionId - Stripe Checkout Session ID
 */
export async function confirmStripeCheckout(sessionId) {
  const base = getBaseUrl();
  const params = new URLSearchParams({ session_id: sessionId });
  const url = `${base}/bot/api/subscription/confirm-checkout?${params}`.replace(/([^:]\/)\/+/g, '$1');
  const res = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return parseJson(res);
}

export async function getProrationPreview(planId, billingCycle) {
  const base = getBaseUrl();
  const params = new URLSearchParams({ plan_id: planId, billing_cycle: billingCycle });
  const url = `${base}/bot/api/subscription/proration-preview?${params}`.replace(/([^:]\/)\/+/g, '$1');
  const res = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return parseJson(res);
}
